import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import Navbar from "./components/Navbar";
import LoadingOverlay from "./components/LoadingOverlay";
import { useLoading } from "./contexts/LoadingContext";
import { useSubscription } from "./contexts/SubscriptionContext";
import { usePurchaseSetup } from "./hooks/usePurchaseSetup";
import { useRevenueCat } from "./hooks/useRevenueCat";
import { injectLoadingSetter } from "./utils/apiClient";
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import AppLayout from './AppLayout';
import { useAuth } from "./contexts/authContext";
import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app'; // Import App and URLOpenListenerEvent

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Preferences from "./pages/Preferences";
import ChatPage from "./pages/ChatPage";
import Favourites from "./pages/Favourites";
import RecipeDetail from "./pages/RecipeDetail";
import RecipeShared from "./pages/RecipeShared";
import CookMode from "./pages/CookMode";
import CookModeV2 from "./pages/CookModev2";
import MealPlanPage from "./pages/MealPlanPage";
import MealPlanListPage from "./pages/MealPlanListPage";
import MealPlanCookMode from "./pages/MealPlanCookMode";
import MealPlanShoppingList from "./pages/MealPlanShoppingList";
import Landing from "./pages/Landing";
import Recipes from "./pages/Recipes";
import ConversationList from "./pages/ConversationList";
import MealPlanEditPage from "./pages/MealPlanEditPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Help from "./pages/Help";
import CalendarPage from "./pages/CalendarPage";
import WeeklyShoppingList from "./pages/WeeklyShoppingList";
import Home from "./pages/Home";
import OnboardAboutYou from "./pages/onboard/AboutYou";
import OnboardYourKitchen from "./pages/onboard/YourKitchen";
import OnboardYourMeals from "./pages/onboard/YourMeals";
import OnboardWelcome from "./pages/onboard/Welcome";
import OnboardGettingStarted from "./pages/onboard/GettingStarted";
import SubscribePage from "./pages/SubscribePage";
import { useCapacitorBackButtonHandler } from './hooks/useCapacitorBackButtonHandler';
import AboutYouPage from "./pages/preferences/AboutYou";
import YourKitchenPage from "./pages/preferences/YourKitchen";
import YourMealsPage from "./pages/preferences/YourMeals";
import AccountPage from "./pages/AccountPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import SubscribeSuccess from './pages/SubscribeSuccess';
import MyRecipes from './pages/MyRecipes';
import SousRecipes from './pages/SousRecipes';
import LogoAnimation from './components/logoAnimation/LogoAnimation';

import { SafeArea } from '@capacitor-community/safe-area';

function App() {
  const { token, isLoggedIn } = useAuth();
  const { setIsLoading } = useLoading();
  const { isSubscribed, isRcReady } = useSubscription();
  const navigate = useNavigate(); // Get the navigate function from React Router

  useEffect(() => {
    // Only call on device (and typically only needed on Android)
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      SafeArea.enable({
        config: {
          customColorsForSystemBars: true,
          statusBarColor: '#6B9080',
          statusBarContent: 'light',
          navigationBarColor: '#00000000',
          navigationBarContent: 'light',
          // offset: 0, // optional
        },
      });
    }
  }, []);

  useEffect(() => {
      if (Capacitor.getPlatform() === 'ios') { // change to ios check only?  Capacitor.getPlatform() === 'ios'  Capacitor.isNativePlatform()
        Keyboard.setResizeMode({ mode: 'native' });
        Keyboard.setAccessoryBarVisible({ isVisible: false });
      }
  }, []);

  usePurchaseSetup();  // comment out until ready to bring in billing
  useRevenueCat();      // RevenueCat
  useCapacitorBackButtonHandler();

  useEffect(() => {
    injectLoadingSetter(setIsLoading);
  }, [setIsLoading]);


    // Deep Link Handling
    useEffect(() => {
      CapacitorApp.addListener('appUrlOpen', (event) => {
        // Example: If your deep link is `your-app-scheme://recipe/123` or `https://yourdomain.com/recipe/123`
        // We want to extract '/recipe/123'
        const url = new URL(event.url);
        const path = url.pathname;
        const host = url.host; // Useful for universal/app links
  
        console.log('App opened with URL:', event.url);
        console.log('Path:', path);
        console.log('Host:', host);
  
        // Implement your routing logic based on the path
        // This is a simplified example, you might need more complex parsing
        if (path) {
          // You can add more specific logic here if certain deep links should only be accessible when logged in, subscribed, etc.
  
          // Example: Handle /recipe/:id deep links
          if (path.startsWith('/recipe/')) {
            const recipeId = path.split('/recipe/')[1];
            if (recipeId) {
              navigate(`/recipe/${recipeId}`);
            }
          }
          // Example: Handle /recipe-share/:id deep links
          else if (path.startsWith('/recipe-share/')) {
            const recipeId = path.split('/recipe-share/')[1];
            if (recipeId) {
              navigate(`/recipe-share/${recipeId}`);
            }
          }
          // Example: Handle /reset-password/:token deep links
          else if (path.startsWith('/reset-password/')) {
            const token = path.split('/reset-password/')[1];
            if (token) {
              navigate(`/reset-password/${token}`);
            }
          }
          // Add more `else if` conditions for other deep link paths
          // For example, if you have a custom scheme like `myapp://chat/123`
          // you might parse `event.url` directly, or if it's a web link, use `path`
          else {
            // Fallback for general paths if needed
            navigate(path);
          }
        }
      });
  
      // Clean up the listener when the component unmounts
      return () => {
        CapacitorApp.removeAllListeners();
      };
    }, [navigate]); // Add navigate to dependency array


  // ✅ Block routes until subscription status is known
  if (isLoggedIn && (isSubscribed === null || !isRcReady)) {
    //return null; // silently block
    //return <div className="text-center p-4 pt-12 text-gray-500">Loading ...</div>;
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-sage">
        <LogoAnimation />
      </div>
    );
  }

  return (
    
<Routes>
      {/* App Shell with Navbar and Layout */}
      <Route path="/" element={<AppLayout/>}>

        {/* Public or Home depending on login */}
        <Route
          index
          element={isLoggedIn ? <Home /> : <Landing />}
        />
        <Route path="/landing" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/subscribe-success" element={<SubscribeSuccess />} />

        {/* Onboarding */}
        <Route path="/onboard/about-you" element={isLoggedIn ? <OnboardAboutYou token={token} /> : <Navigate to="/" />} />
        <Route path="/onboard/your-kitchen" element={isLoggedIn ? <OnboardYourKitchen token={token} /> : <Navigate to="/" />} />
        <Route path="/onboard/your-meals" element={isLoggedIn ? <OnboardYourMeals token={token} /> : <Navigate to="/" />} />
        <Route path="/onboard/welcome" element={isLoggedIn ? <OnboardWelcome token={token} /> : <Navigate to="/" />} />
        <Route path="/onboard/getting-started" element={isLoggedIn ? <OnboardGettingStarted token={token} /> : <Navigate to="/" />} />

        {/* Subscription */}
        <Route path="/subscribe" element={<SubscribePage />} />

        {/* Protected Routes */}
        {[
          { path: "/preferences", component: Preferences },
          { path: "/preferences/about-you", component: AboutYouPage },
          { path: "/preferences/your-kitchen", component: YourKitchenPage },
          { path: "/preferences/your-meals", component: YourMealsPage },
          { path: "/chat", component: ChatPage },
          { path: "/chat/:id", component: ChatPage },
          //{ path: "/favourites", component: Favourites },
          { path: "/recipe/:index", component: RecipeDetail },
          { path: "/mealplan/:id", component: MealPlanPage },
          { path: "/mealplans", component: MealPlanListPage },
          { path: "/edit/mealplan/:id", component: MealPlanEditPage },
          { path: "/cook/mealplan/:id", component: MealPlanCookMode },
          { path: "/shopping/mealplan/:id", component: MealPlanShoppingList },
          //{ path: "/recipes", component: Recipes },
          { path: "/recipes", component: MyRecipes },
          { path: "/conversations", component: ConversationList },
          { path: "/calendar", component: CalendarPage },
          { path: "/ingredients/week/:startDate", component: WeeklyShoppingList },
          { path: "/account", component: AccountPage },
          { path: "/sous-recipes", component: SousRecipes },
        ].map(({ path, component: Component }) => (
          <Route
            key={path}
            path={path}
            element={
              isLoggedIn ? (
                // ✅ Allow all logged-in users to access these routes.
                // The components themselves will check `isSubscribed` and enforce limits.
                <Component token={token} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        ))}

        {/* Unprotected */}
        <Route path="/recipe-share/:id" element={<RecipeShared />} />
        <Route path="/cook/:id" element={<CookModeV2 />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/help" element={<Help />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Route>
    </Routes>
    
  );
}

export default App;
